"use strict";
/**
 * Shared shape of the public casting sign-up at /casting (NEW-1088).
 *
 * The page renders these lists and the backend validates against them, so
 * they live here rather than being typed twice and drifting. The questions
 * mirror the Airtable "New Creator Form Sign up" the casting team sends
 * creators today, so the form can replace it without changing what they ask.
 *
 * Values are the casting roster's own enum names wherever the roster has one.
 * The three answers it has no value for ("Non-binary", "Prefer not to say",
 * "I have a partner") are mapped on the server and the original answer is
 * kept in the creator's notes, so nothing a creator chose is lost.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CASTING_SIGNUP_HEADSHOT_MAX_BYTES = exports.CASTING_SIGNUP_HEADSHOT_MIME_TYPES = exports.CASTING_SIGNUP_MAX_LENGTH = exports.CASTING_SIGNUP_INTERESTS = exports.CASTING_SIGNUP_RELATIONSHIP_LABELS = exports.CASTING_SIGNUP_RELATIONSHIPS = exports.CASTING_SIGNUP_KIDS_LABELS = exports.CASTING_SIGNUP_KIDS = exports.CASTING_SIGNUP_LANGUAGE_LABELS = exports.CASTING_SIGNUP_LANGUAGES = exports.CASTING_SIGNUP_ETHNICITY_LABELS = exports.CASTING_SIGNUP_ETHNICITIES = exports.CASTING_SIGNUP_AGE_RANGE_LABELS = exports.CASTING_SIGNUP_AGE_RANGES = exports.CASTING_SIGNUP_GENDER_LABELS = exports.CASTING_SIGNUP_GENDERS = void 0;
exports.castingSignupUploadHeaders = castingSignupUploadHeaders;
exports.CASTING_SIGNUP_GENDERS = [
    "MALE",
    "FEMALE",
    "NON_BINARY",
    "TRANSGENDER",
    "PREFER_NOT_TO_SAY",
    "OTHER",
];
exports.CASTING_SIGNUP_GENDER_LABELS = {
    MALE: "Male",
    FEMALE: "Female",
    NON_BINARY: "Non-binary",
    TRANSGENDER: "Transgender",
    PREFER_NOT_TO_SAY: "Prefer not to say",
    OTHER: "Other",
};
exports.CASTING_SIGNUP_AGE_RANGES = [
    "AGE_18_24",
    "AGE_25_29",
    "AGE_30_34",
    "AGE_35_39",
    "AGE_40_49",
    "AGE_50_PLUS",
];
exports.CASTING_SIGNUP_AGE_RANGE_LABELS = {
    AGE_18_24: "18–24",
    AGE_25_29: "25–29",
    AGE_30_34: "30–34",
    AGE_35_39: "35–39",
    AGE_40_49: "40–49",
    AGE_50_PLUS: "50+",
};
exports.CASTING_SIGNUP_ETHNICITIES = [
    "WHITE",
    "BLACK_AFRICAN_AMERICAN",
    "LATINO_HISPANIC",
    "MIDDLE_EASTERN_NORTH_AFRICAN",
    "SOUTH_ASIAN",
    "EAST_ASIAN",
    "SOUTHEAST_ASIAN",
    "NATIVE_INDIGENOUS",
    "OTHER",
];
exports.CASTING_SIGNUP_ETHNICITY_LABELS = {
    WHITE: "White",
    BLACK_AFRICAN_AMERICAN: "Black / African American",
    LATINO_HISPANIC: "Latino / Hispanic",
    MIDDLE_EASTERN_NORTH_AFRICAN: "Middle Eastern / North African",
    SOUTH_ASIAN: "South Asian",
    EAST_ASIAN: "East Asian",
    SOUTHEAST_ASIAN: "Southeast Asian",
    NATIVE_INDIGENOUS: "Native / Indigenous",
    OTHER: "Other",
};
exports.CASTING_SIGNUP_LANGUAGES = [
    "ENGLISH",
    "SPANISH",
    "MANDARIN",
    "KOREAN",
    "JAPANESE",
    "ARABIC",
    "FRENCH",
    "HINDI",
    "PORTUGUESE",
    "FILIPINO",
    "GERMAN",
    "ITALIAN",
    "RUSSIAN",
    "URDU",
];
exports.CASTING_SIGNUP_LANGUAGE_LABELS = {
    ENGLISH: "English",
    SPANISH: "Spanish",
    MANDARIN: "Mandarin Chinese",
    KOREAN: "Korean",
    JAPANESE: "Japanese",
    ARABIC: "Arabic",
    FRENCH: "French",
    HINDI: "Hindi",
    PORTUGUESE: "Portuguese",
    FILIPINO: "Tagalog / Filipino",
    GERMAN: "German",
    ITALIAN: "Italian",
    RUSSIAN: "Russian",
    URDU: "Urdu",
};
exports.CASTING_SIGNUP_KIDS = [
    "NO",
    "YES_INFANT",
    "YES_CHILD",
    "YES_TEEN",
];
exports.CASTING_SIGNUP_KIDS_LABELS = {
    NO: "No",
    YES_INFANT: "Yes, infant",
    YES_CHILD: "Yes, child",
    YES_TEEN: "Yes, teen",
};
exports.CASTING_SIGNUP_RELATIONSHIPS = [
    "SINGLE",
    "IN_A_RELATIONSHIP",
    "MARRIED",
    "PARTNER",
];
exports.CASTING_SIGNUP_RELATIONSHIP_LABELS = {
    SINGLE: "Single",
    IN_A_RELATIONSHIP: "In a relationship",
    MARRIED: "Married",
    PARTNER: "I have a partner",
};
/** Kept as the labels themselves: the roster has no column for interests, so
 * they are written into the creator's notes as the words the creator picked. */
exports.CASTING_SIGNUP_INTERESTS = [
    "Fitness",
    "AI",
    "Tech",
    "Cooking",
    "Beauty",
    "Music",
    "Comedy",
    "Fashion",
    "Gaming",
    "Finance",
    "Parenting",
    "Education",
    "Medical",
    "Sports",
    "Food",
];
exports.CASTING_SIGNUP_MAX_LENGTH = {
    name: 200,
    email: 254,
    phone: 40,
    link: 500,
    location: 200,
    occupation: 200,
    shortAnswer: 1000,
    notes: 2000,
};
/** Browsers hand iPhone photos over as JPEG when HEIC is not in `accept`, so
 * these three cover a phone's camera roll without a conversion step. */
exports.CASTING_SIGNUP_HEADSHOT_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];
/** Matches the console's own headshot cap (MAX_HEADSHOT_FILE_SIZE). */
exports.CASTING_SIGNUP_HEADSHOT_MAX_BYTES = 8 * 1024 * 1024;
/**
 * The storage headers the headshot upload URL is signed with: a body of
 * exactly `size` bytes, and no object already at the path. GCS refuses the PUT
 * unless the browser sends these same values, so the server's signature and the
 * page's request are built from this one function and cannot drift apart. The
 * bucket's CORS rules must also list both names as allowed headers.
 */
function castingSignupUploadHeaders(size) {
    return {
        "x-goog-if-generation-match": "0",
        "x-goog-content-length-range": `${size},${size}`,
    };
}
